import { TriangleAlert } from "lucide-react";

import { joinParts } from "../../lib/vocabulary";

/**
 * ONE territory renderer. RC-10.
 *
 * NINE OUTPUTS FOR ONE CONDITION. A doctor, a distributor, a team member and a
 * map card each have a territory unit, and each rendered a missing one
 * differently: "No territory", "—", `"—"` inside literal quotes as a group
 * heading, "No territory unit — visible to no officer", the element omitted
 * entirely, an empty suffix, and — twice — a blank followed by the separator
 * that was supposed to come after it, so a doctor with no unit read ", Patna"
 * and a visit read "Cardiology · ".
 *
 * AND THE WARNING FIRED ONCE. `territory_unmigrated` marks a record whose
 * territory is a legacy free-text string the master does not recognise. Five
 * surfaces render that string as though it were a unit; only the Team roster
 * card said so. The Director dashboard, the manager dashboard, the field map,
 * team attendance and the doctor pages showed "HQ" or "Patna-Head Office"
 * looking exactly like "Patna East".
 *
 * PROMOTED FROM: `TerritoryUnitSelect`, which had the right idea on the edit
 * side — an unrecognised value is still shown, under an amber "Current (not an
 * active unit)" label, because a record must display what it actually holds —
 * and the Team roster card's wording, which this reuses verbatim so the string
 * a test pins in one place is the string every surface renders.
 *
 * SEPARATORS BELONG BETWEEN THINGS THAT EXIST. `joinParts` drops the empties,
 * which is the whole of the orphan-comma family of defects.
 */

/** The single missing-value string. Not "—", which reads as a value. */
export const NO_TERRITORY = "No territory unit";

/** The needs-review sentence, promoted from the Team roster card (F457). */
export function needsReviewText(value) {
  return `Territory needs review — ${value ? `"${value}"` : "this record's value"} is not a unit`;
}

/**
 * @param value        the stored territory / unit name
 * @param unmigrated   the record's `territory_unmigrated` flag
 * @param suffixes     extra address parts (city, pin) — joined without orphans
 * @param hint         a surface-specific consequence, e.g. "visible to no
 *                     officer" on distributors. Wording varies; the rendering
 *                     does not.
 * @param review       "inline" (default) shows the sentence; "icon" shows the
 *                     triangle only, for dense rows; false suppresses it on
 *                     surfaces that carry their own remediation UI.
 */
export default function TerritoryLabel({
  value,
  unmigrated = false,
  suffixes = [],
  hint = "",
  review = "inline",
  className = "",
  testId,
}) {
  const clean = typeof value === "string" ? value.trim() : value;
  const missing = clean === null || clean === undefined || clean === "";
  const line = joinParts([missing ? null : clean, ...suffixes], ", ");

  if (missing) {
    return (
      <span
        className={`text-callisto-muted ${className}`}
        data-testid={testId || undefined}
      >
        {joinParts([NO_TERRITORY, hint], " — ")}
        {suffixes.length > 0 && joinParts(suffixes, ", ")
          ? ` · ${joinParts(suffixes, ", ")}`
          : ""}
      </span>
    );
  }

  return (
    <span className={className} data-testid={testId || undefined}>
      <span className={unmigrated ? "text-amber-700" : ""}>{line}</span>
      {unmigrated && review === "inline" && (
        <span className="flex items-center gap-1 text-[11px] text-amber-700 font-semibold mt-0.5">
          <TriangleAlert size={11} /> {needsReviewText(clean)}
        </span>
      )}
      {unmigrated && review === "icon" && (
        <TriangleAlert
          size={11}
          className="inline ml-1 text-amber-700"
          aria-label={needsReviewText(clean)}
        />
      )}
    </span>
  );
}
